<template>
  <div class="space-y-4">
    <!-- Phase 1: Request OTP -->
    <div v-if="!otpSent" class="space-y-4 text-left">
      <div class="space-y-1.5">
        <label
          for="identifier"
          class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
        >
          Mobile Number or Email
        </label>
        <div
          class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
        >
          <span class="pl-3.5 pr-2 text-slate-400 dark:text-slate-500 flex items-center pointer-events-none">
            <Smartphone v-if="!isIdentifierEmail" class="w-4 h-4 text-indigo-500" />
            <Mail v-else class="w-4 h-4 text-indigo-500" />
          </span>
          <input
            ref="identifierInputRef"
            id="identifier"
            v-model.trim="identifier"
            type="text"
            autocomplete="username"
            placeholder="e.g. 9876543210 or you@school.com"
            class="w-full py-3 pr-3.5 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none"
            @keydown.enter.prevent="requestOtp"
          />
        </div>
        <p class="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 pt-0.5">
          <Shield class="w-3 h-3 text-emerald-500" />
          <span>We'll send a 6-digit code via WhatsApp, SMS, or Email</span>
        </p>
      </div>

      <!-- Alerts -->
      <transition name="fade">
        <div
          v-if="error"
          class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 animate-shake"
        >
          <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
          <span class="flex-1 leading-snug">{{ error }}</span>
        </div>
      </transition>

      <transition name="fade">
        <div
          v-if="info"
          class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200 dark:border-indigo-900/50 text-indigo-700 dark:text-indigo-300"
        >
          <CheckCircle2 class="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-500" />
          <span class="flex-1 leading-snug">{{ info }}</span>
        </div>
      </transition>

      <!-- Action Button -->
      <button
        @click="requestOtp"
        :disabled="isRequesting || !identifier"
        class="w-full mt-2 bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-3 px-4 rounded-xl active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-transparent"
      >
        <Loader2 v-if="isRequesting" class="w-4 h-4 animate-spin" />
        <Send v-else class="w-4 h-4" />
        <span>{{ isRequesting ? "Dispatching OTP..." : "Send Verification Code" }}</span>
      </button>
    </div>

    <!-- Phase 2: Verify OTP -->
    <div v-else class="space-y-4 text-left">
      <!-- Target Indicator Chip -->
      <div
        class="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80"
      >
        <div class="flex items-center gap-2.5 min-w-0">
          <div class="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-950/80 text-indigo-600 dark:text-indigo-400 flex items-center justify-center flex-shrink-0">
            <KeyRound class="w-4 h-4" />
          </div>
          <div class="min-w-0">
            <p class="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Code sent to
            </p>
            <p class="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
              {{ maskedTarget }}
            </p>
          </div>
        </div>

        <button
          type="button"
          @click="resetToRequest"
          class="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-2.5 py-1.5 rounded-lg hover:bg-indigo-50 dark:hover:bg-slate-700 transition-colors"
        >
          <Edit2 class="w-3 h-3" />
          <span>Change</span>
        </button>
      </div>

      <!-- OTP Input -->
      <div class="space-y-1.5">
        <label
          for="otp-input"
          class="block text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider"
        >
          6-Digit Verification Code
        </label>
        <div
          class="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-700/80 bg-slate-50/70 dark:bg-slate-800/50 focus-within:bg-white dark:focus-within:bg-slate-800 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 dark:focus-within:border-indigo-400 transition-all duration-200"
        >
          <input
            ref="otpInputRef"
            id="otp-input"
            type="text"
            inputmode="numeric"
            pattern="[0-9]*"
            maxlength="6"
            v-model.trim="otp"
            autocomplete="one-time-code"
            placeholder="000000"
            class="w-full py-3.5 px-4 bg-transparent text-center tracking-[0.4em] font-mono text-xl font-bold text-slate-900 dark:text-slate-100 placeholder:text-slate-300 dark:placeholder:text-slate-600 focus:outline-none"
            @keydown.enter.prevent="verifyOtp"
          />
        </div>
      </div>

      <!-- Resend & Helper Row -->
      <div class="flex items-center justify-between text-xs pt-0.5">
        <span class="text-slate-500 dark:text-slate-400">Didn't get the code?</span>
        <button
          type="button"
          class="font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          :disabled="resendCooldown > 0 || isRequesting"
          @click="resendOtp"
        >
          <span>Resend OTP</span>
          <span v-if="resendCooldown > 0" class="ml-1 font-mono">({{ resendCooldown }}s)</span>
        </button>
      </div>

      <!-- Alerts -->
      <transition name="fade">
        <div
          v-if="error"
          class="flex items-start gap-2.5 p-3 rounded-xl text-xs sm:text-sm bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 text-rose-700 dark:text-rose-300 animate-shake"
        >
          <AlertCircle class="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-500" />
          <span class="flex-1 leading-snug">{{ error }}</span>
        </div>
      </transition>

      <!-- Verify Button -->
      <button
        @click="verifyOtp"
        :disabled="isVerifying || otp.length !== 6"
        class="w-full mt-2 bg-gradient-to-r from-indigo-600 via-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold py-3.5 px-4 rounded-xl active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 border border-transparent"
      >
        <Loader2 v-if="isVerifying" class="w-4 h-4 animate-spin" />
        <CheckCircle2 v-else class="w-4 h-4" />
        <span>{{ isVerifying ? "Verifying Token..." : "Verify & Sign In" }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, watch, nextTick } from "vue";
import {
  Smartphone,
  Mail,
  Send,
  Loader2,
  KeyRound,
  Edit2,
  AlertCircle,
  CheckCircle2,
  Shield,
} from "lucide-vue-next";

const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

const props = defineProps({
  requestEndpoint: { type: String, default: "/api/auth/login/send-otp" },
  verifyEndpoint: { type: String, default: "/api/auth/login/verify-otp" },
});

const emit = defineEmits(["success", "cancel"]);

const identifier = ref("");
const otp = ref("");
const otpSent = ref(false);
const target = ref("");

const isRequesting = ref(false);
const isVerifying = ref(false);
const error = ref("");
const info = ref("");
const otpInputRef = ref(null);
const identifierInputRef = ref(null);

const isIdentifierEmail = computed(() => identifier.value.includes("@"));

const maskedTarget = computed(() => {
  if (!target.value) return "";
  if (target.value.includes("@")) {
    return target.value.replace(/(.{2}).+(@.+)/, "$1****$2");
  }
  const digits = target.value.replace(/\D/g, "");
  if (digits.length >= 10) {
    return digits.slice(0, 2) + "******" + digits.slice(-2);
  }
  return target.value;
});

let timer = null;
const resendCooldown = ref(60);
const startCooldown = () => {
  clearInterval(timer);
  resendCooldown.value = 60;
  timer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) clearInterval(timer);
  }, 1000);
};
onBeforeUnmount(() => clearInterval(timer));

const resetToRequest = async () => {
  otpSent.value = false;
  otp.value = "";
  error.value = "";
  info.value = "";
  clearInterval(timer);
  resendCooldown.value = 0;

  await nextTick();
  identifierInputRef.value?.focus();
};

const requestOtp = async () => {
  error.value = "";
  info.value = "";

  if (!identifier.value) {
    error.value = "Please enter your mobile number or email.";
    return;
  }

  try {
    isRequesting.value = true;

    const isEmail = identifier.value.includes("@");
    const phoneDigits = isEmail
      ? undefined
      : identifier.value.replace(/\D/g, "").slice(-10);

    const res = await fetch(`${baseURL}${props.requestEndpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: isEmail ? identifier.value.trim() : undefined,
        phone: isEmail ? undefined : phoneDigits || identifier.value.trim(),
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "Failed to send OTP.");

    target.value = identifier.value;
    const firstTime = !otpSent.value;

    otpSent.value = true;
    info.value = data?.message || "OTP sent successfully.";
    if (data?.devOtp) {
      otp.value = data.devOtp;
    }
    startCooldown();

    if (!firstTime) {
      await nextTick();
      setTimeout(() => {
        otpInputRef.value?.focus();
      }, 50);
    }
  } catch (e) {
    error.value = e.message || "Failed to send OTP. Please try again.";
  } finally {
    isRequesting.value = false;
  }
};

const resendOtp = async () => {
  if (resendCooldown.value > 0) return;
  await requestOtp();
};

const verifyOtp = async () => {
  error.value = "";
  if (otp.value.length !== 6) {
    error.value = "Please enter the 6-digit OTP code.";
    return;
  }

  try {
    isVerifying.value = true;

    const isEmail = identifier.value.includes("@");
    const phoneDigits = isEmail
      ? undefined
      : identifier.value.replace(/\D/g, "").slice(-10);

    const res = await fetch(`${baseURL}${props.verifyEndpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: isEmail ? identifier.value.trim() : undefined,
        phone: isEmail ? undefined : phoneDigits || identifier.value.trim(),
        otp: otp.value,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || "OTP verification failed.");

    emit("success", { token: data.token, user: data.user });
  } catch (e) {
    error.value = e.message || "OTP verification failed. Please try again.";
  } finally {
    isVerifying.value = false;
  }
};

watch(otp, async (value) => {
  if (value.length === 6 && otpSent.value && !isVerifying.value) {
    await verifyOtp();
  }
});

watch(otpSent, async (val) => {
  if (val) {
    await nextTick();
    setTimeout(() => {
      otpInputRef.value?.focus();
    }, 50);
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  75% { transform: translateX(4px); }
}
.animate-shake {
  animation: shake 0.3s ease-in-out;
}
</style>
